import React, { useState, useEffect } from 'react';
import heroImg from '../assets/floor-scraper-hero.jpg';
import Logo from '../components/ui/Logo';
import img1 from '../assets/floor-scraper-img1.jpg';
import img2 from '../assets/floor-scraper-img2.jpg';
import img3 from '../assets/floor-scraper-img3.png';
import img4 from '../assets/floor-scraper-img4.png';
import img5 from '../assets/floor-scraper-img5.png';
import img6 from '../assets/floor-scraper-img6.png';

const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

const TIMELINE_OPTIONS = [
     'ASAP',
     'This week',
     'Next 2 weeks',
     'This month',
     'Just exploring',
];

function InquiryForm({ trackingParams }) {
     const [form, setForm] = useState({
          name: '',
          email: '',
          phone: '',
          project_timeline: '',
          project_description: '',
     });
     const [errors, setErrors] = useState({});
     const [submitting, setSubmitting] = useState(false);
     const [submitted, setSubmitted] = useState(false);

     function validate() {
          const errs = {};
          if (!form.name.trim()) errs.name = 'Name is required';
          if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
               errs.email = 'Valid email is required';
          if (!form.phone.trim()) errs.phone = 'Phone is required';
          if (!form.project_timeline) errs.project_timeline = 'Select a timeline';
          return errs;
     }

     async function handleSubmit(e) {
          e.preventDefault();
          const errs = validate();
          if (Object.keys(errs).length > 0) {
               setErrors(errs);
               return;
          }
          setErrors({});
          setSubmitting(true);

          try {
               const res = await fetch(`${SUPABASE_URL}/functions/v1/submit-inquiry`, {
                    method: 'POST',
                    headers: {
                         'Content-Type': 'application/json',
                         apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
                    },
                    body: JSON.stringify({
                         ...form,
                         ...trackingParams,
                         source_url: window.location.href,
                    }),
               });

               if (res.ok) {
                    setSubmitted(true);
                    // Google Ads conversion tracking placeholder
                    // Replace AW-XXXXX/YYYYY with your Conversion ID and Label
                    if (window.gtag) {
                         window.gtag('event', 'conversion', {
                              send_to: 'AW-XXXXX/YYYYY',
                         });
                    }
               }
          } catch {
               // Silent fail — form still shows
          }
          setSubmitting(false);
     }

     const inputClasses =
          'w-full bg-darker border border-dark rounded-lg px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-yellow duration-300';

     if (submitted) {
          return (
               <div className='bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-8 text-center'>
                    <h3 className='text-yellow text-2xl font-bold mb-2'>Request Received!</h3>
                    <p className='text-white/60'>
                         We'll get back to you shortly to discuss your project.
                    </p>
               </div>
          );
     }

     return (
          <form onSubmit={handleSubmit} className='bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-6 space-y-4'>
               <h3 className='text-white text-xl font-bold mb-2'>Request Your Rental</h3>
               <div>
                    <input
                         type='text'
                         placeholder='Full Name'
                         value={form.name}
                         onChange={(e) => setForm({ ...form, name: e.target.value })}
                         className={inputClasses}
                    />
                    {errors.name && <p className='text-red-400 text-sm mt-1'>{errors.name}</p>}
               </div>
               <div>
                    <input
                         type='email'
                         placeholder='Email Address'
                         value={form.email}
                         onChange={(e) => setForm({ ...form, email: e.target.value })}
                         className={inputClasses}
                    />
                    {errors.email && <p className='text-red-400 text-sm mt-1'>{errors.email}</p>}
               </div>
               <div>
                    <input
                         type='tel'
                         placeholder='Phone Number'
                         value={form.phone}
                         onChange={(e) => setForm({ ...form, phone: e.target.value })}
                         className={inputClasses}
                    />
                    {errors.phone && <p className='text-red-400 text-sm mt-1'>{errors.phone}</p>}
               </div>
               <div>
                    <select
                         value={form.project_timeline}
                         onChange={(e) => setForm({ ...form, project_timeline: e.target.value })}
                         className={inputClasses + (!form.project_timeline ? ' text-white/30' : '')}
                    >
                         <option value=''>Project Timeline</option>
                         {TIMELINE_OPTIONS.map((opt) => (
                              <option key={opt} value={opt}>
                                   {opt}
                              </option>
                         ))}
                    </select>
                    {errors.project_timeline && (
                         <p className='text-red-400 text-sm mt-1'>{errors.project_timeline}</p>
                    )}
               </div>
               <div>
                    <textarea
                         placeholder='Describe your project (sq ft, flooring type, etc.)'
                         value={form.project_description}
                         onChange={(e) => setForm({ ...form, project_description: e.target.value })}
                         className={inputClasses + ' h-24 resize-none'}
                    />
               </div>
               <button
                    type='submit'
                    disabled={submitting}
                    className='w-full bg-yellow text-black font-bold py-3 rounded-lg hover:bg-yellow/90 duration-300 disabled:opacity-50'
               >
                    {submitting ? 'Submitting...' : 'Get Started'}
               </button>
               <p className='text-white/30 text-xs text-center'>
                    Protected by our{' '}
                    <a
                         href='/privacy'
                         target='_blank'
                         rel='noopener noreferrer'
                         className='text-white/50 underline hover:text-white/70 duration-300'
                    >
                         Privacy Policy
                    </a>
               </p>
          </form>
     );
}

export default function FloorScraperRental() {
     const [trackingParams, setTrackingParams] = useState({});

     useEffect(() => {
          const params = new URLSearchParams(window.location.search);
          setTrackingParams({
               gclid: params.get('gclid') || '',
               utm_source: params.get('utm_source') || '',
               utm_medium: params.get('utm_medium') || '',
               utm_campaign: params.get('utm_campaign') || '',
               utm_term: params.get('utm_term') || '',
               utm_content: params.get('utm_content') || '',
          });
     }, []);

     return (
          <div className='min-h-screen bg-black text-white'>
               {/* Hero */}
               <section className='relative'>
                    <div
                         className='absolute inset-0 bg-cover bg-center opacity-30'
                         style={{ backgroundImage: `url(${heroImg})` }}
                    />
                    {/* Header - transparent, overlays hero */}
                    <header className='relative z-10'>
                         <div className='max-w-6xl mx-auto px-4 py-4 flex items-center justify-between'>
                              <Logo className='w-36' />
                              <a
                                   href='tel:+16023624644'
                                   className='bg-yellow text-black font-bold px-5 py-2 rounded-lg hover:bg-yellow/90 duration-300 text-sm'
                              >
                                   Call Now: (602) 362-4644
                              </a>
                         </div>
                    </header>
                    <div className='relative max-w-6xl mx-auto px-4 py-16 tablet:py-24'>
                         <div className='grid tablet:grid-cols-2 gap-12 items-start'>
                              <div>
                                   <h1 className='text-4xl tablet:text-5xl font-bold leading-tight mb-6'>
                                        Rent a Ride-On Floor Scraper{' '}
                                        <span className='text-yellow'>+ Trained Operator</span>
                                   </h1>
                                   <p className='text-2xl text-yellow font-bold mb-4'>$1,299/Day</p>
                                   <p className='text-white/60 text-lg mb-8'>
                                        Delivery included. We show up to your project with the equipment
                                        and a trained operator. Servicing all East Valley metro areas.
                                   </p>
                                   <ul className='space-y-3 text-white/80'>
                                        <li className='flex items-start gap-3'>
                                             <span className='text-yellow font-bold text-lg'>&#10003;</span>
                                             <span>Professional ride-on floor scraper with trained operator</span>
                                        </li>
                                        <li className='flex items-start gap-3'>
                                             <span className='text-yellow font-bold text-lg'>&#10003;</span>
                                             <span>Delivery and setup included in the price</span>
                                        </li>
                                        <li className='flex items-start gap-3'>
                                             <span className='text-yellow font-bold text-lg'>&#10003;</span>
                                             <span>Fast turnaround — book today, scrape tomorrow</span>
                                        </li>
                                        <li className='flex items-start gap-3'>
                                             <span className='text-yellow font-bold text-lg'>&#10003;</span>
                                             <span>Hardwood, thinset, carpet, VCT — Let our machine do the work</span>
                                        </li>
                                   </ul>
                              </div>
                              <InquiryForm trackingParams={trackingParams} />
                         </div>
                    </div>
               </section>

               {/* Video Section */}
               <section className='bg-darker py-16'>
                    <div className='max-w-6xl mx-auto px-4'>
                         <h2 className='text-3xl font-bold text-center mb-8'>
                              See It In <span className='text-yellow'>Action</span>
                         </h2>
                         <div className='aspect-video bg-dark rounded-xl border border-dark flex items-center justify-center'>
                              <p className='text-white/30 text-lg'>Video Placeholder</p>
                         </div>
                    </div>
               </section>

               {/* How It Works */}
               <section className='py-16'>
                    <div className='max-w-6xl mx-auto px-4'>
                         <h2 className='text-3xl font-bold text-center mb-12'>
                              How It <span className='text-yellow'>Works</span>
                         </h2>
                         <div className='grid tablet:grid-cols-3 gap-8'>
                              {[
                                   {
                                        step: '1',
                                        title: 'Request a Rental',
                                        desc: 'Fill out the form with your project details and preferred timeline.',
                                   },
                                   {
                                        step: '2',
                                        title: 'Get Your Proposal',
                                        desc: "We'll review your project and send a proposal for your approval.",
                                   },
                                   {
                                        step: '3',
                                        title: 'We Show Up & Scrape',
                                        desc: 'Our team delivers the equipment and operator to your job site.',
                                   },
                              ].map((item) => (
                                   <div key={item.step} className='bg-darker border border-dark rounded-xl p-6 text-center'>
                                        <div className='w-12 h-12 bg-yellow text-black rounded-full flex items-center justify-center font-bold text-xl mx-auto mb-4'>
                                             {item.step}
                                        </div>
                                        <h3 className='text-white font-bold text-lg mb-2'>{item.title}</h3>
                                        <p className='text-white/60'>{item.desc}</p>
                                   </div>
                              ))}
                         </div>
                    </div>
               </section>

               {/* Image Gallery Placeholder */}
               <section className='bg-darker py-16'>
                    <div className='max-w-6xl mx-auto px-4'>
                         <h2 className='text-3xl font-bold text-center mb-8'>
                              Your Rental <span className='text-yellow'>Results</span>
                         </h2>
                         <div className='grid grid-cols-2 tablet:grid-cols-3 gap-4'>
                              {[img1, img2, img3, img4, img5, img6].map((src, i) => (
                                   <div
                                        key={i}
                                        className='aspect-video bg-dark rounded-xl border border-dark overflow-hidden'
                                   >
                                        <img
                                             src={src}
                                             alt={`Floor scraper result ${i + 1}`}
                                             className='w-full h-full object-cover'
                                        />
                                   </div>
                              ))}
                         </div>
                    </div>
               </section>

               {/* Bottom CTA */}
               <section className='py-16'>
                    <div className='max-w-xl mx-auto px-4'>
                         <h2 className='text-3xl font-bold text-center mb-8'>
                              Ready to <span className='text-yellow'>Get Started?</span>
                         </h2>
                         <InquiryForm trackingParams={trackingParams} />
                    </div>
               </section>

               {/* Minimal Footer */}
               <footer className='py-8 border-t border-dark'>
                    <div className='max-w-6xl mx-auto px-4 flex flex-col items-center gap-2'>
                         <a href='/'>
                              <Logo className='w-36 opacity-30 hover:opacity-60 duration-300' />
                         </a>
                         <span className='text-white/30 text-xs'>
                              Ripout Rentals&reg; | Phoenix, Arizona |{' '}
                              <a href='/privacy' className='underline hover:text-white/50 duration-300'>
                                   Privacy Policy
                              </a>
                         </span>
                    </div>
               </footer>
          </div>
     );
}
